import 'react-toastify/dist/ReactToastify.css'
import '../styles/globals.css'
import '../utils/Extensions'

import ReactPixel, { AdvancedMatching } from '@juanlatorre/react-facebook-pixel'
import { appUtils } from 'lib/AppUtils'
import { setupInterceptors } from 'lib/axios'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo,useRef, useState } from 'react'
import { hotjar } from 'react-hotjar'
import { ToastContainer } from 'react-toastify'
import useSWR from 'swr'

import ScrollTopButton from '@/components/buttons/ScrollTopButton'
import PodscribeScript from '@/components/script/PodscribeScript'
import TaboolaScript from '@/components/script/TaboolaScript'
import { useAuth } from '@/hooks/Auth'
import {
  defaultScriptState,
  defaultStats,
  defaultSystemConfig,
  LanguageContext,
  Layout,
  LayoutContext,
  MyCartContext,
  ReferralContext,
  ScriptStateContext,
  StatsContext,
  SystemConfigContext,
  UserContext,
} from '@/hooks/Contexts'
import { useGA } from '@/hooks/GoogleAnalytics'
import { useGTM } from '@/hooks/GoogleTagManager'
import { useMounted } from '@/hooks/Index'
import { CartCount } from '@/models/Response'
import { API_URL } from '@/utils/Consts'
import { deepReplaceStrings, GaWordReplacement } from '@/utils/GaLanding'
import { safeSessionStorage } from '@/utils/SafeStorage'

import english from '../public/lang/en.json'
const Maintenance = dynamic(() => import('./maintenance'))
const Footer = dynamic(() => import('../components/Navigation/Footer'))
const Navbar = dynamic(() => import('../components/Navigation/Navbar'))
const Notification = dynamic(() => import('../components/Notification'))
const FeedbackButton = dynamic(() => import('../components/Feedback/FeedbackButton'), { ssr: false })

function MyApp(props: any) {
  const [system, setSystem] = useState(defaultSystemConfig)
  const [layout, setLayout] = useState<Layout>({} as Layout)
  const [strings] = useState(english)
  const [stats, setStats] = useState(defaultStats)
  const [refCtx, setRefCtx] = useState<Record<string, any>>({})
  const [scriptState, setScriptState] = useState(defaultScriptState)
  const [showScrollTopBtn, setShowScrollTopBtn] = useState<number>(null)

  const router = useRouter()
  const { user, logout } = useAuth()
  useEffect(() => setupInterceptors(logout), [])

  const isMounted = useMounted()

  const { initGTM, brazeIdentify } = useGTM()
  const { initGA } = useGA()
  const identifiedUserIdRef = useRef<string | number | null>(null)

  useEffect(() => {
    initGTM()
    initGA()

    if (process.env.NEXT_PUBLIC_HOT_JAR_ID) {
      const hotId = process.env.NEXT_PUBLIC_HOT_JAR_ID
      hotjar.initialize(Number.parseInt(hotId), 6)
    }

    // Facebook pixel tracking
    if (process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_EMAIL) {
      const advancedMatching = {
        em: process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_EMAIL,
      } as AdvancedMatching
      const options = { autoConfig: true, debug: false }
      if (typeof window !== 'undefined') {
        ReactPixel.init(process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID, advancedMatching, options)
        ReactPixel.init(process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID2, advancedMatching, options)
        ReactPixel.init(process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID3, advancedMatching, options)
        ReactPixel.fbq('trackSingle', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID, 'PageView')
        ReactPixel.fbq('trackSingle', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID2, 'PageView')
        ReactPixel.fbq('trackSingle', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID3, 'PageView')
      }
    }

    /**
     * System related
     */
    if (typeof window !== 'undefined')
      setSystem(prev => ({
        ...prev,
        isAndroid: /android/i.test(navigator.userAgent || navigator.vendor),
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent.toLowerCase()),
        screen: { width: window.innerWidth, height: window.innerHeight },
      }))

    appUtils
      .fetcher(API_URL.stats, false)
      .then(res => {
        if (res) setStats(res)
      })
      .catch(() => {})

    if (scriptState.flexMicroformScript === 'notloaded') {
      appUtils.evaluateRemoteScript(
        process.env.NEXT_PUBLIC_FLEX_MICROFORM,
        () => {
          if (!isMounted) {
            return
          }
          setScriptState(s => ({ ...s, flexMicroformScript: 'loaded' }))
        },
        () => {
          if (!isMounted) {
            return
          }
          setScriptState(s => ({ ...s, flexMicroformScript: 'loading' }))
        }
      )
    }
  }, [])

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', function () {
        navigator.serviceWorker
          .register('/serviceWorker.js')
          .then()
          .catch(() => {})
        // function (registration) {
        //   console.log('registration successful with scope: ', registration.scope)
        // },
        // function (err) {
        //   console.log('registration failed: ', err)
        // }
      })
    }
  }, [])

  /**
   * Layout Context
   */
  const resizeListener = useCallback(() => {
    const windowWidth = window?.innerWidth
    setLayout({
      isDesktop: windowWidth >= 768 ? true : false,
      breakpoint:
        windowWidth >= 1200
          ? '2xl'
          : windowWidth >= 1140
          ? 'xl'
          : windowWidth >= 992
          ? 'lg'
          : windowWidth >= 768
          ? 'md'
          : windowWidth >= 576
          ? 'sm'
          : windowWidth >= 375
          ? 'xs'
          : '2xs',
    })
  }, [])

  const scrollListener = useCallback(() => setShowScrollTopBtn((window?.scrollY || 0) >= (window?.innerHeight || 0) * 0.5 ? window?.innerWidth : null), [])

  useEffect(() => {
    resizeListener()
    scrollListener()
    window.addEventListener('resize', resizeListener)
    window.addEventListener('scroll', scrollListener)
    return () => {
      window.removeEventListener('resize', resizeListener)
      window.removeEventListener('scroll', scrollListener)
    }
  }, [])

  // After a new deploy, a client-side navigation can still reference a JS chunk hash that
  // no longer exists on the CDN, which surfaces as an unhandled rejection with no recovery
  // path (RKINGS-2BN). Force a one-time hard reload to fetch the current build instead.
  useEffect(() => {
    // Covers both failure modes seen in Sentry: the dynamic import() rejecting outright
    // (RKINGS-2BN), and a truncated/corrupted chunk download failing to parse, which
    // surfaces as a plain window "error" event instead of a promise rejection (RKINGS-4Z8).
    // 解析失败的措辞按浏览器分:Chrome 是 "Unexpected end of input",Safari 是
    // "Unexpected end of script"。这里刻意不匹配 "Unexpected token" —— 那会把 JSON.parse
    // 拿到 HTML(弱网/运营商劫持/captive portal 的典型响应)也当成坏 chunk,把用户页面白刷一次。
    const CHUNK_ERROR_PATTERN =
      /Importing a module script failed|Failed to fetch dynamically imported module|Loading chunk .* failed|Loading CSS chunk .* failed|Unexpected end of (input|script)/
    const RELOAD_FLAG = 'chunkErrorReload'
    const RELOAD_WINDOW_MS = 5 * 60 * 1000
    const MAX_RELOADS = 2
    // 「这是谁的脚本坏了」不能靠消息文本猜 —— 同一族已经踩过三次:
    //   1. 上线前:曾匹配 `Unexpected token`,任何 JSON.parse 拿到 HTML 都会白刷一次(已去掉)
    //   2. RKINGS-506:Apple CDN 的动态 import 失败,报文与我们自己的坏 chunk 一字不差
    //   3. RKINGS-400:Facebook 站内浏览器注入脚本被截断,报文只有 `Unexpected end of input`,
    //      连 URL 都没有(出事脚本其实是 `iabjs://browser_declutter`),发生在竞赛页→购物车路径上
    // 所以判据换成「出事脚本的真实地址」:window error 事件带 `filename`,优先用它;
    // 拿不到再退回消息里的 URL;两者都没有就不刷新 —— 宁可少救一次,也不要白刷用户页面。
    //
    // 唯一例外是 webpack 运行时那两句:只有我们自己的 bundle 会产出,第三方脚本产不出来,
    // 所以即便没有来源信息也可以放心刷新。
    const OWN_RUNTIME_PATTERN = /Loading chunk .* failed|Loading CSS chunk .* failed/
    const isOwnOrigin = (candidate?: string) => {
      if (!candidate) return false
      try {
        // 带 base 解析,这样 `/_next/static/chunks/42.js` 这种相对地址也能判成同源;
        // `iabjs://browser_declutter` 之类的自定义协议 origin 是 "null",判不同源
        return new URL(candidate, window.location.href).origin === window.location.origin
      } catch (e) {
        return false
      }
    }
    const handleChunkError = (reason: any, source?: string) => {
      const message = reason?.message || String(reason || '')
      if (!CHUNK_ERROR_PATTERN.test(message)) return
      if (!OWN_RUNTIME_PATTERN.test(message) && !isOwnOrigin(source) && !isOwnOrigin(message.match(/https?:\/\/[^\s)'"]+/)?.[0])) {
        return
      }
      // CDN 真的坏掉时不要退化成「每隔几秒 reload 一次」的循环:窗口内最多重试 MAX_RELOADS 次,
      // 之后放弃并让错误照常上报 Sentry
      let reload = { count: 0, at: 0 }
      try {
        reload = { ...reload, ...JSON.parse(safeSessionStorage.getItem(RELOAD_FLAG) || '{}') }
      } catch (e) {
        // 标记损坏就当没重试过
      }
      const withinWindow = Date.now() - reload.at < RELOAD_WINDOW_MS
      if (withinWindow && reload.count >= MAX_RELOADS) return
      safeSessionStorage.setItem(RELOAD_FLAG, JSON.stringify({ count: withinWindow ? reload.count + 1 : 1, at: Date.now() }))
      window.location.reload()
    }
    const onUnhandledRejection = (event: PromiseRejectionEvent) => handleChunkError(event.reason)
    // 只处理「chunk 下载完了但解析失败」这一种:它既不产生 promise rejection,Next 自己也不兜底。
    // chunk 请求在网络层彻底失败的情况不用管 —— 实测 Next router 会自己 hard-navigate 恢复;
    // 而 resource error 事件本身不带 message,靠 event.target 判断只会写出永不生效的死代码。
    // `event.filename` 就是出事脚本的地址,这是判同源最可靠的信号(RKINGS-400 里它是
    // `iabjs://browser_declutter`,而报文里什么线索都没有)
    const onParseError = (event: ErrorEvent) => handleChunkError(event.error || event.message, event.filename)
    window.addEventListener('unhandledrejection', onUnhandledRejection)
    window.addEventListener('error', onParseError)
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      window.removeEventListener('error', onParseError)
    }
  }, [])

  /**
   * Spy on next.router route change
   */
  useEffect(() => {
    const handleRouteChange = url => {
      const isNotAuthRoute = ['/maintenance', '/404', '/login', '/register', '/forget-password', '/reset-password', '/user', '/cart/'].every(
        route => !router.pathname.startsWith(route)
      )
      // Current page is not auth page, also, Target page is login or register
      if (isNotAuthRoute && (url === '/login' || url === '/register')) {
        safeSessionStorage.setItem('pathBeforeAuth', router.asPath)
      } else {
        // Current page is auth routes already or Target page is not login nor register
        // Do nothing
        return
      }
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.pathname])

  /*
   * 不套站点外壳的路由。
   *
   * /checkout 本来就是这么做的（结账页不要导航干扰）。/ga 是付费搜索落地页：
   * 站点的导航和页脚本身就带着广告政策不收的词（Competitions / Draws /
   * Winners / Entry List），套上去等于把落地页特意避开的词又加回来 ——
   * 实测套壳渲染出来有 6 类触发词，全部来自导航和页脚，正文一个都没有。
   */
  /*
   * 页头/页脚什么时候不渲染。
   *
   * /checkout 一直是全关的。/ga/ 这个落地页不一样：页头页脚现在是**可选的**，
   * 由后台那份 disabled_blocks 决定 —— 站点导航本身带着广告政策不收的词
   * （Competitions / Draws / Winners / Entry List），要不要留由投放那边判断。
   *
   */
  const isGaLanding = router.pathname.startsWith('/ga/')
  const gaBlocks: string[] | null = (props?.pageProps as { gaBlocks?: string[] | null })?.gaBlocks ?? null
  const gaWords = (props?.pageProps as { gaWords?: GaWordReplacement[] })?.gaWords ?? []

  /*
   * 落地页模式下，整份文案字典先过一遍后台配的词表。
   *
   * 不在 JSX 里逐个包字符串，是因为那样一定会漏 —— 实测漏掉了 Tickets / Winner /
   * DRAW TODAY 这些写在块内部的标签。从字典这一层改，页面、导航、页脚只要读字典就都覆盖到。
   *
   * 替换在服务端和客户端用的是同一份 props，所以两边渲染结果一致，
   * 不会出现 hydration 之后文字又翻回原词的情况。
   */
  const langStrings = useMemo(
    () => (isGaLanding && gaWords.length ? deepReplaceStrings(strings, gaWords) : strings),
    [isGaLanding, gaWords, strings],
  )
  const hideChrome = (block: string) => router.pathname.includes('/checkout') || (isGaLanding && !!gaBlocks?.includes(block))
  const bareLayout = router.pathname.includes('/checkout') || isGaLanding

  // Get referral code via query or cookie
  useEffect(() => {
    try {
      if (!router.isReady) {
        return
      } else if (router.query?.rc) {
        const { rc, ...rest } = router.query
        router.push({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
        const rcStr = rc.toString()
        setRefCtx(prev => ({ ...prev, rc: rcStr }))
        document.cookie = `rkrc=${rcStr};path=/;sameSite=lax;max-age=${60 * 60 * 24 * 7}`
        appUtils.postData(API_URL.campaignHit, { rc: rcStr }, strings.errors, false)
      } else if (!refCtx.rc) {
        const rcCookie = document.cookie
          ?.split?.(';')
          ?.find?.(item => item.trim().startsWith('rkrc='))
          ?.split?.('=')?.[1]
        if (rcCookie) {
          setRefCtx(prev => ({ ...prev, rc: rcCookie }))
        }
      }
      const { utm_content, utm_source, utm_campaign, fbclid } = router.query
      if (router.isReady && utm_content && utm_source && fbclid) {
        const fbCampaignObj = utm_content + '.' + utm_source + '.' + utm_campaign
        document.cookie = `_fbCO=${encodeURIComponent(fbCampaignObj)};path=/;domain=${appUtils.getTopLevelDomain()};sameSite=lax;max-age=${60 * 60 * 24 * 7}`
        appUtils.postData(API_URL.adsClick, { details: fbCampaignObj }, strings.errors, false).catch(() => {})
      }
      if (router.isReady && fbclid && !utm_content) {
        document.cookie = `_fbCO=;path=/;domain=${appUtils.getTopLevelDomain()};sameSite=lax;max-age=1`
      }
    } catch (error) {
      console.log(error)
    }
  }, [router.query])

  const { data: cartCount } = useSWR<CartCount>(API_URL.cartCount, appUtils.fetcher, {
    shouldRetryOnError: false,
    focusThrottleInterval: 60000,
    onError: err => {
      if (err && err.response) {
        if (err.response.status === 503) {
          if (!system.isMaintain) {
            setSystem(prev => ({ ...prev, isMaintain: true }))
          }
        } else if (system.isMaintain) {
          setSystem(prev => ({ ...prev, isMaintain: false }))
        }
      }
      // else if ('Network Error' == err.message) {
      //   if (!system.isMaintain) {
      //     setSystem(prev => ({ ...prev, isMaintain: true }))
      //   }
      // }
    },
    errorRetryCount: 5,
  })

  // Braze identify - only call once per user ID to avoid duplicate calls
  useEffect(() => {
    if (user?.id && identifiedUserIdRef.current !== user.id) {
      identifiedUserIdRef.current = user.id
      brazeIdentify(user.id)
    } else if (!user?.id) {
      // Reset when user logs out
      identifiedUserIdRef.current = null
    }
  }, [user?.id, brazeIdentify])

  return (
    <SystemConfigContext.Provider value={system}>
      <LayoutContext.Provider value={layout}>
        <LanguageContext.Provider value={langStrings}>
          {system.isMaintain ? (
            <Maintenance />
          ) : (
            <>
              <StatsContext.Provider value={stats}>
                <ReferralContext.Provider value={refCtx}>
                  <UserContext.Provider value={user}>
                    <MyCartContext.Provider value={cartCount}>
                      <ScriptStateContext.Provider value={scriptState}>
                        {hideChrome('header') ? null : <Navbar />}
                        {router.pathname == '/cart/[orderId]' ? null : <Notification />}
                        <props.Component {...props?.pageProps} />
                        {bareLayout ? null : <ScrollTopButton isActive={showScrollTopBtn} />}
                        {bareLayout ? null : <FeedbackButton />}
                        {hideChrome('footer') ? null : <Footer />}
                        <ToastContainer className='min-w-fit text-sm md:text-base' position='top-center' limit={3} />
                      </ScriptStateContext.Provider>
                    </MyCartContext.Provider>
                  </UserContext.Provider>
                </ReferralContext.Provider>
              </StatsContext.Provider>
              <TaboolaScript />
              <PodscribeScript />
            </>
          )}
        </LanguageContext.Provider>
      </LayoutContext.Provider>
    </SystemConfigContext.Provider>
  )
}

MyApp.propTypes = {
  Component: PropTypes.elementType,
  pageProps: PropTypes.object,
}

export default MyApp
